import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GalleryUpload } from "@/components/image-upload";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from "@/lib/payment-methods";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const HIGHLIGHT_BADGE_VALUES = ["", "new", "popular", "recommended", "limited"] as const;

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  description: z.string().optional(),
  price: z.coerce.number().min(0, "Cena musi być >= 0"),
  durationMinutes: z.coerce.number().int().min(1).optional().or(z.literal("")),
  status: z.enum(["active", "inactive", "draft"]),
  highlightBadge: z.enum(HIGHLIGHT_BADGE_VALUES),
  /** `false` = the treatment inherits the salon's payment methods. */
  customPaymentMethods: z.boolean(),
  paymentMethods: z.array(z.enum(PAYMENT_METHODS)),
}).refine((d) => !d.customPaymentMethods || d.paymentMethods.length > 0, {
  path: ["paymentMethods"],
  message: "Wybierz co najmniej jedną metodę płatności",
});

type FormData = z.infer<typeof schema>;

type HighlightBadge = "new" | "popular" | "recommended" | "limited";

type Treatment = {
  id: string;
  name: string;
  description: string | null;
  price: number;
  durationMinutes: number | null;
  status: "active" | "inactive" | "draft";
  sortOrder: number;
  highlightBadge?: HighlightBadge | null;
  paymentMethods?: PaymentMethod[] | null;
};

const TAB_PANEL = "flex min-h-0 flex-1 flex-col";

export function TreatmentDialog({
  open,
  onOpenChange,
  treatment,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  treatment?: Treatment | null;
  onCreated?: (t: Treatment) => void;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!treatment;
  const [activeTab, setActiveTab] = useState("details");

  const createMutation = trpc.treatment.create.useMutation({
    onSuccess: (data) => {
      utils.treatment.list.invalidate();
      toast.success("Zabieg utworzony — dodaj zdjęcia");
      // Switch to edit mode with images tab
      onCreated?.({
        id: data.id,
        name: data.name,
        description: data.description,
        price: data.price,
        durationMinutes: data.durationMinutes,
        status: data.status as "active" | "inactive" | "draft",
        sortOrder: data.sortOrder,
        highlightBadge: data.highlightBadge,
        paymentMethods: data.paymentMethods,
      });
      setActiveTab("images");
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.treatment.update.useMutation({
    onSuccess: () => {
      utils.treatment.list.invalidate();
      toast.success("Zabieg zaktualizowany");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  // Images (only for existing treatments)
  const { data: images } = trpc.treatment.getImages.useQuery(
    { treatmentId: treatment?.id ?? "" },
    { enabled: !!treatment?.id },
  );

  const addImage = trpc.treatment.addImage.useMutation({
    onSuccess: () =>
      utils.treatment.getImages.invalidate({
        treatmentId: treatment?.id ?? "",
      }),
  });

  const removeImage = trpc.treatment.removeImage.useMutation({
    onSuccess: () =>
      utils.treatment.getImages.invalidate({
        treatmentId: treatment?.id ?? "",
      }),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      durationMinutes: "",
      status: "draft",
      highlightBadge: "",
      customPaymentMethods: false,
      paymentMethods: [],
    },
  });

  useEffect(() => {
    if (open) {
      setActiveTab("details");
      if (treatment) {
        form.reset({
          name: treatment.name,
          description: treatment.description ?? "",
          price: treatment.price / 100,
          durationMinutes: treatment.durationMinutes ?? "",
          status: treatment.status,
          highlightBadge: treatment.highlightBadge ?? "",
          customPaymentMethods: !!treatment.paymentMethods,
          paymentMethods: treatment.paymentMethods ?? [],
        });
      } else {
        form.reset({
          name: "",
          description: "",
          price: 0,
          durationMinutes: "",
          status: "draft",
          highlightBadge: "",
          customPaymentMethods: false,
          paymentMethods: [],
        });
      }
    }
  }, [open, treatment, form]);

  // Shares the cache entry with `PaymentMethodsField`.
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();

  const onSubmit = form.handleSubmit((data) => {
    // A method the salon has switched off since the treatment was saved is
    // already ineffective, and the server refuses a list that names it. Drop
    // it here so that editing any other field doesn't fail on a stale value
    // the owner would first have to find and uncheck.
    let paymentMethods: PaymentMethod[] | null = null;
    if (data.customPaymentMethods) {
      paymentMethods = salon
        ? data.paymentMethods.filter((m) => salon.paymentMethods.includes(m))
        : data.paymentMethods;
      if (paymentMethods.length === 0) {
        form.setError("paymentMethods", {
          message: "Wybierz co najmniej jedną metodę płatności",
        });
        return;
      }
    }

    const payload = {
      name: data.name,
      description: data.description || null,
      price: Math.round(data.price * 100),
      durationMinutes:
        data.durationMinutes !== "" && data.durationMinutes !== undefined
          ? Number(data.durationMinutes)
          : null,
      highlightBadge: (data.highlightBadge || null) as HighlightBadge | null,
      paymentMethods,
    };

    if (isEdit && treatment) {
      updateMutation.mutate({ id: treatment.id, status: data.status, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const title = (
    <DialogTitle>{isEdit ? "Edytuj zabieg" : "Nowy zabieg"}</DialogTitle>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {isEdit ? (
          // `contents` lets the tab list sit in the fixed header while each
          // panel brings its own scrolling body and footer. The panels stay
          // real boxes so screen readers keep their tabpanel role.
          <Tabs value={activeTab} onValueChange={setActiveTab} className="contents">
            <DialogHeader className="gap-4">
              {title}
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">
                  Dane
                </TabsTrigger>
                <TabsTrigger value="addons" className="flex-1">
                  Dodatki
                </TabsTrigger>
                <TabsTrigger value="images" className="flex-1">
                  Zdjęcia
                </TabsTrigger>
              </TabsList>
            </DialogHeader>

            <TabsContent value="details" className={TAB_PANEL}>
              <TreatmentForm
                form={form}
                onSubmit={onSubmit}
                isPending={isPending}
                isEdit
                onCancel={() => onOpenChange(false)}
              />
            </TabsContent>

            <TabsContent value="addons" className={TAB_PANEL}>
              <DialogBody>
                <AddonsManager treatmentId={treatment!.id} />
              </DialogBody>
            </TabsContent>

            <TabsContent value="images" className={TAB_PANEL}>
              <DialogBody className="space-y-4">
                <div className="space-y-2">
                  <Label>Zdjęcie główne</Label>
                  <GalleryUpload
                    images={
                      images
                        ?.filter((i) => i.type === "main")
                        .map((i) => ({ id: i.id, url: i.url })) ?? []
                    }
                    onAdd={(url) =>
                      addImage.mutate({
                        treatmentId: treatment!.id,
                        url,
                        type: "main",
                      })
                    }
                    onRemove={(id) => removeImage.mutate({ imageId: id })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Galeria</Label>
                  <GalleryUpload
                    images={
                      images
                        ?.filter((i) => i.type === "gallery")
                        .map((i) => ({ id: i.id, url: i.url })) ?? []
                    }
                    onAdd={(url) =>
                      addImage.mutate({
                        treatmentId: treatment!.id,
                        url,
                        type: "gallery",
                      })
                    }
                    onRemove={(id) => removeImage.mutate({ imageId: id })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Przed / Po</Label>
                  <GalleryUpload
                    images={
                      images
                        ?.filter((i) => i.type === "before_after")
                        .map((i) => ({ id: i.id, url: i.url })) ?? []
                    }
                    onAdd={(url) =>
                      addImage.mutate({
                        treatmentId: treatment!.id,
                        url,
                        type: "before_after",
                      })
                    }
                    onRemove={(id) => removeImage.mutate({ imageId: id })}
                  />
                </div>
              </DialogBody>
              <DialogFooter>
                <Button type="button" onClick={() => onOpenChange(false)}>
                  Gotowe
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        ) : (
          <>
            <DialogHeader>{title}</DialogHeader>
            <TreatmentForm
              form={form}
              onSubmit={onSubmit}
              isPending={isPending}
              isEdit={false}
              onCancel={() => onOpenChange(false)}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TreatmentForm({
  form,
  onSubmit,
  isPending,
  isEdit,
  onCancel,
}: {
  form: ReturnType<typeof useForm<FormData>>;
  onSubmit: () => void;
  isPending: boolean;
  isEdit: boolean;
  onCancel: () => void;
}) {
  return (
    <DialogForm onSubmit={onSubmit}>
      <DialogBody className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="t-name">Nazwa</Label>
          <Input id="t-name" {...form.register("name")} />
          {form.formState.errors.name && (
            <p className="text-sm text-destructive">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="t-desc">Opis</Label>
          <Textarea
            id="t-desc"
            rows={6}
            className="max-h-[40vh] min-h-36"
            {...form.register("description")}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="t-price">Cena (zł)</Label>
            <Input
              id="t-price"
              type="number"
              step="0.01"
              min="0"
              {...form.register("price")}
            />
            {form.formState.errors.price && (
              <p className="text-sm text-destructive">
                {form.formState.errors.price.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="t-dur">Czas trwania (min)</Label>
            <Input
              id="t-dur"
              type="number"
              min="1"
              {...form.register("durationMinutes")}
            />
          </div>
        </div>
        {isEdit && (
          <div className="space-y-2">
            <Label htmlFor="t-status">Status</Label>
            <select
              id="t-status"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              {...form.register("status")}
            >
              <option value="draft">Szkic</option>
              <option value="active">Aktywny</option>
              <option value="inactive">Nieaktywny</option>
            </select>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="t-highlight">Wyróżnienie</Label>
          <select
            id="t-highlight"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            {...form.register("highlightBadge")}
          >
            <option value="">Brak</option>
            <option value="new">Nowe</option>
            <option value="popular">Popularne</option>
            <option value="recommended">Polecane</option>
            <option value="limited">Limitowane</option>
          </select>
          <p className="text-xs text-muted-foreground">
            Pokazuje się jako etykieta na karcie zabiegu w aplikacji mobilnej.
            Jeśli zabieg ma aktywną ofertę, jej badge ma pierwszeństwo.
          </p>
        </div>
        <PaymentMethodsField form={form} />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Anuluj
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending
            ? "Zapisywanie..."
            : isEdit
              ? "Zapisz zmiany"
              : "Utwórz zabieg"}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}

// ── Payment methods ───────────────────────────────────────────────────────

/** Lets a treatment narrow the salon's payment methods — typically down to
 *  "pay at the salon only". Only methods the salon offers can be picked. */
function PaymentMethodsField({
  form,
}: {
  form: ReturnType<typeof useForm<FormData>>;
}) {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  const salonMethods: readonly PaymentMethod[] = salon?.paymentMethods ?? [];
  const custom = form.watch("customPaymentMethods");
  const selected = form.watch("paymentMethods");
  const error = form.formState.errors.paymentMethods?.message;

  const setSelected = (next: PaymentMethod[]) =>
    form.setValue("paymentMethods", next, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });

  return (
    <div className="space-y-2">
      <Label htmlFor="t-payment-mode">Metody płatności</Label>
      <select
        id="t-payment-mode"
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
        value={custom ? "custom" : "inherit"}
        onChange={(e) => {
          const isCustom = e.target.value === "custom";
          form.setValue("customPaymentMethods", isCustom, { shouldDirty: true });
          // "Pick at least one" only applies to a custom list. Clear just that
          // error rather than re-validating the whole form, which would flag
          // fields the owner has not touched yet.
          if (!isCustom) form.clearErrors("paymentMethods");
          // Start from what the client sees today, so narrowing is one click.
          if (isCustom && selected.length === 0) setSelected([...salonMethods]);
        }}
      >
        <option value="inherit">Takie jak w ustawieniach salonu</option>
        <option value="custom">Wybrane dla tego zabiegu</option>
      </select>
      {custom && (
        <div className="space-y-2 rounded-md border p-3">
          {PAYMENT_METHODS.map((method) => {
            const offered = salonMethods.includes(method);
            const checked = selected.includes(method);
            // A method the salon has since switched off stays uncheckable-only:
            // it can be removed here, but not added.
            if (!offered && !checked) return null;
            return (
              <div key={method} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`t-pm-${method}`}
                  className="h-4 w-4 rounded border-gray-300"
                  checked={checked}
                  onChange={(e) =>
                    setSelected(
                      PAYMENT_METHODS.filter((m) =>
                        m === method ? e.target.checked : selected.includes(m),
                      ),
                    )
                  }
                />
                <Label htmlFor={`t-pm-${method}`}>
                  {PAYMENT_METHOD_LABELS[method]}
                  {!offered && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (wyłączona w salonie — zostanie usunięta przy zapisie)
                    </span>
                  )}
                </Label>
              </div>
            );
          })}
          {!salonMethods.includes("onsite") && (
            <p className="text-xs text-muted-foreground">
              Płatność w salonie jest wyłączona — włącz ją w Ustawieniach, aby
              móc ją tu wybrać.
            </p>
          )}
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <p className="text-xs text-muted-foreground">
        Zawęź metody dla tego zabiegu, np. zostaw tylko „Płatność w salonie”,
        jeśli nie chcesz przyjmować za niego płatności online.
      </p>
      {custom && <LegacyAppNote />}
    </div>
  );
}

/** Owners should know what clients who have not updated the app yet will see. */
export function LegacyAppNote() {
  return (
    <p className="text-xs text-muted-foreground">
      Klienci ze starszą wersją aplikacji nadal zobaczą wszystkie metody
      płatności. Wyłączonej metody nie da się u nich użyć — zamówienie zostanie
      odrzucone, a w opisie zabiegu pojawi się informacja o dostępnych metodach
      i prośba o aktualizację.
    </p>
  );
}

// ── Addons manager ────────────────────────────────────────────────────────

const addonSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  price: z.coerce.number().min(0, "Cena musi być >= 0"),
});

type AddonFormData = z.infer<typeof addonSchema>;

function AddonsManager({ treatmentId }: { treatmentId: string }) {
  const utils = trpc.useUtils();
  const { data: addons, isLoading } = trpc.treatment.getAddons.useQuery({ treatmentId });
  const [editingId, setEditingId] = useState<string | null>(null);

  const createAddon = trpc.treatment.createAddon.useMutation({
    onSuccess: () => {
      utils.treatment.getAddons.invalidate({ treatmentId });
      toast.success("Dodatek dodany");
    },
    onError: (err) => toast.error(err.message),
  });

  const updateAddon = trpc.treatment.updateAddon.useMutation({
    onSuccess: () => {
      utils.treatment.getAddons.invalidate({ treatmentId });
      setEditingId(null);
      toast.success("Dodatek zaktualizowany");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteAddon = trpc.treatment.deleteAddon.useMutation({
    onSuccess: () => {
      utils.treatment.getAddons.invalidate({ treatmentId });
      toast.success("Dodatek usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  const addForm = useForm<AddonFormData>({
    resolver: zodResolver(addonSchema),
    defaultValues: { name: "", price: 0 },
  });

  const editForm = useForm<AddonFormData>({
    resolver: zodResolver(addonSchema),
  });

  const onAdd = addForm.handleSubmit((data) => {
    createAddon.mutate({
      treatmentId,
      name: data.name,
      price: Math.round(data.price * 100),
    });
    addForm.reset({ name: "", price: 0 });
  });

  const startEdit = (addon: { id: string; name: string; price: number }) => {
    setEditingId(addon.id);
    editForm.reset({ name: addon.name, price: addon.price / 100 });
  };

  const onEdit = editForm.handleSubmit((data) => {
    if (!editingId) return;
    updateAddon.mutate({
      addonId: editingId,
      name: data.name,
      price: Math.round(data.price * 100),
    });
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-4">
      {addons && addons.length > 0 && (
        <div className="space-y-2">
          {addons.map((addon) => (
            <div key={addon.id}>
              {editingId === addon.id ? (
                <form onSubmit={onEdit} className="flex items-end gap-2">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Nazwa</Label>
                    <Input {...editForm.register("name")} />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">Cena (zł)</Label>
                    <Input type="number" step="0.01" min="0" {...editForm.register("price")} />
                  </div>
                  <Button type="submit" size="sm" disabled={updateAddon.isPending}>
                    Zapisz
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                    Anuluj
                  </Button>
                </form>
              ) : (
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <span className="font-medium">{addon.name}</span>
                    <span className="ml-2 text-sm text-muted-foreground">
                      {(addon.price / 100).toFixed(2)} zł
                    </span>
                    {!addon.isActive && (
                      <span className="ml-2 text-xs text-muted-foreground">(nieaktywny)</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(addon)}>
                      Edytuj
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm("Usunąć dodatek?")) {
                          deleteAddon.mutate({ addonId: addon.id });
                        }
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={onAdd} className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Nazwa dodatku</Label>
          <Input placeholder="np. Maska nawilżająca" {...addForm.register("name")} />
        </div>
        <div className="w-28 space-y-1">
          <Label className="text-xs">Cena (zł)</Label>
          <Input type="number" step="0.01" min="0" {...addForm.register("price")} />
        </div>
        <Button type="submit" size="sm" disabled={createAddon.isPending}>
          <Plus className="mr-1 size-4" />
          Dodaj
        </Button>
      </form>
    </div>
  );
}
